import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { safeReturnPath } from "@shared/auth/safe-return-path";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";

interface InvitationPayload {
  email: string;
  firstName: string | null;
  lastName: string | null;
  userType: "admin" | "agent" | "client";
}

export default function AcceptInvitePage() {
  const [, navigate] = useLocation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token"),
    [],
  );
  const returnTo = useMemo(
    () => safeReturnPath(new URLSearchParams(window.location.search).get("returnTo")),
    [],
  );

  const { data, isLoading } = useQuery<InvitationPayload>({
    queryKey: [`/api/auth/invitations/${token}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: Boolean(token),
    retry: false,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");

    if (!token) {
      setError("Missing invitation token.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await apiRequest("POST", `/api/auth/invitations/${token}/accept`, {
        password,
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to accept invitation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Accept invitation</CardTitle>
          <CardDescription>
            Set your password to activate your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!token && (
            <Alert variant="destructive">
              <AlertTitle>Invalid invitation</AlertTitle>
              <AlertDescription>
                This invite link is missing its token.
              </AlertDescription>
            </Alert>
          )}

          {token && isLoading && <p className="text-sm text-muted-foreground">Loading invitation...</p>}

          {token && data && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                <p className="font-medium">
                  {[data.firstName, data.lastName].filter(Boolean).join(" ") || data.email}
                </p>
                <p className="text-muted-foreground">{data.email}</p>
                <p className="text-muted-foreground capitalize">{data.userType}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-password">Password</Label>
                <Input
                  id="invite-password"
                  type="password"
                  minLength={8}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-confirm-password">Confirm password</Label>
                <Input
                  id="invite-confirm-password"
                  type="password"
                  minLength={8}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Activating..." : "Activate account"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
